// client/src/components/UserHydrationGate/index.tsx
import { useApolloClient } from '@apollo/client';
import { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { AUTH_CONSTANTS } from '../../constants/auth.constants';
import { AuthContext } from '../../context/AuthContext';
import { useWebSocketLogout } from '../../context/WebsocketContext';
import { GET_USER_QUERY } from '../../graphql/resolvers/userQueries/getUser.gql';
import type { CoreUserDDB, UserState } from '../../interfaces/user.interface';
import { getAvatarUrl } from '../../services/getPresignedUrlREST';
import { userSlice } from '../../store/user.store';
import { userAvatarSlice } from '../../store/userAvatar.store';

/**
 * TRIL-147: Owns both directions of localStorage auth sync.
 *
 * Mounts once at app shell. On boot, if a JWT is present (AuthContext
 * isSignedIn=true) but Redux user.id is empty, fires GET_USER and
 * dispatches HYDRATE_USER_SUCCESS. On cross-tab logout (storage event
 * removing the access token), disconnects WS + calls AuthContext.logout().
 *
 * Renders nothing. Pure side-effect surface.
 */
const UserHydrationGate = (): null => {
  const { isSignedIn, user: authUser, logout } = useContext(AuthContext);
  const reduxUser = useSelector((state: UserState) => state.userStore.user);
  const dispatch = useDispatch();
  // TRIL-141: identity-check guard reads user.avatar from the live store
  // at commit time so a stale presign cannot win the last write even if it
  // resolves between a Redux state change (logout / new user) and React's
  // passive cleanup. The store is the source of truth at commit time.
  const store = useStore() as {
    getState: () => {
      userStore: { user: { avatar?: string | null } };
    };
  };
  const apolloClient = useApolloClient();
  // TRIL-141: identity-keyed refs. Storing the userId we last hydrated for
  // (rather than a boolean) lets logout→login as a different user re-trigger
  // hydration — the ref's stored id won't match the new reduxUser.id.
  const hasHydratedRef = useRef<string | null>(null);
  const avatarHydratedRef = useRef<string | null>(null);
  const { HYDRATE_USER_SUCCESS } = userSlice.actions;
  const { SET_USER_AVATAR_URL } = userAvatarSlice.actions;

  useEffect(() => {
    if (!isSignedIn) return;
    if (reduxUser.id !== '') return;
    if (hasHydratedRef.current === reduxUser.id) return;
    if (!authUser?.id) return;
    // Write authUser.id rather than reduxUser.id: at this point reduxUser.id
    // is still '' (that's why this effect fires). authUser.id is the JWT-
    // decoded id we're about to hydrate, which becomes reduxUser.id after
    // HYDRATE_USER_SUCCESS dispatches.
    hasHydratedRef.current = authUser.id;

    const BACKOFF_MS = [0, 1000, 2000, 4000] as const; // initial + three retries' delays
    let cancelled = false;
    const attemptHydrate = async (attempt: number): Promise<void> => {
      if (cancelled) return;
      if (attempt >= BACKOFF_MS.length) return; // exhausted
      try {
        const { data } = await apolloClient.query({
          query: GET_USER_QUERY,
          variables: { userId: authUser.id },
          fetchPolicy: 'network-only',
        });
        if (cancelled) return;
        const fetched = data?.getUser;
        // TRIL-304 defense-in-depth: a user object missing `id` (e.g. a future
        // GET_USER selection-set regression) must NOT be written to the store —
        // doing so set `user.id = undefined`, which dead-ended this gate's own
        // `reduxUser.id === ''` re-hydration guard until re-login. Treat a
        // missing id as an invalid user, symmetric with the null case.
        if (!fetched?.id) {
          // TRIL-215: null/missing user from a valid JWT means the user no
          // longer exists in DB (deleted, cross-env stale token, etc.).
          // Silent return used to leave consumers stuck in loading state
          // forever. Treat as logged-out so the user can re-authenticate.
          // Symmetric with the retry-exhausted branch — both warn so the
          // playbook's ops runbook can grep this line.
          console.warn(
            '[UserHydrationGate] GET_USER returned no usable user (null or missing id); logging out',
          );
          await logout();
          return;
        }
        // TRIL-305: `fetched` is now the codegen GetUserQuery type (narrowed
        // non-null by the id guard above). Bridge to the Redux CoreUserDDB shape
        // at this boundary — same pattern as UserLogin's `as CoreUserDDB`. The
        // compile-time protection lives in the `fetched?.id` access above: drop
        // `id` from the getUser selection and that line stops compiling.
        dispatch(HYDRATE_USER_SUCCESS(fetched as unknown as CoreUserDDB));
        // Avatar URL fetch moved to a separate effect (TRIL-147 race fix):
        // dispatching here would race against the re-render this dispatch
        // itself triggers, leaving userAvatarStore unpopulated.
      } catch (err) {
        const isUserNotFound =
          err instanceof Error &&
          (err.message.includes('User ID not found') ||
            // biome-ignore lint/suspicious/noExplicitAny: Apollo error shape not imported here
            (err as any)?.graphQLErrors?.some(
              (e: { extensions?: { code?: string } }) =>
                e?.extensions?.code === 'USER_NOT_FOUND',
            ));
        if (isUserNotFound) {
          await logout();
          return;
        }
        const nextDelay = BACKOFF_MS[attempt + 1];
        if (nextDelay === undefined) {
          // TRIL-215: retries exhausted. Silent bail used to leave consumers
          // (e.g., PostReplyDraft) stuck in loading state forever. Treat as
          // logged-out so the user can re-authenticate.
          console.warn(
            '[UserHydrationGate] hydration retries exhausted; logging out',
          );
          await logout();
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, nextDelay));
        void attemptHydrate(attempt + 1);
      }
    };
    void attemptHydrate(0);

    return () => {
      cancelled = true;
    };
  }, [
    isSignedIn,
    authUser,
    reduxUser.id,
    apolloClient,
    dispatch,
    HYDRATE_USER_SUCCESS,
    logout,
  ]);

  // Avatar URL — separate effect from hydration to avoid the race:
  // HYDRATE_USER_SUCCESS triggers a re-render that cancels the hydration
  // effect's in-flight work. Running the avatar fetch here, gated on
  // reduxUser.avatar (populated by HYDRATE_USER_SUCCESS), keeps the
  // dispatch path alive.
  useEffect(() => {
    if (avatarHydratedRef.current === reduxUser.id) return;

    // TRIL-298: prefer the server-presigned URL already hydrated from GET_USER
    // (userPreferences.avatarUrl) — the same source the login path uses
    // (syncLoginPayloadAvatar). Checked BEFORE the avatar-key guard because the
    // URL doesn't depend on the key, so this also covers a URL-present /
    // key-absent state. The client `getAvatarUrl` presign endpoint is only a
    // fallback; where it's unreachable (e.g. the API Gateway isn't provisioned
    // for the env), a reload would otherwise leave the header avatar blank
    // until re-login.
    //
    // Write reduxUser.id (the dedup key): by the time this effect fires, the
    // hydration effect has already populated reduxUser, so reduxUser.id is the
    // authoritative current user.
    const serverAvatarUrl = reduxUser.userPreferences?.avatarUrl;
    if (serverAvatarUrl) {
      avatarHydratedRef.current = reduxUser.id;
      dispatch(SET_USER_AVATAR_URL(serverAvatarUrl));
      return;
    }

    // Fallback: no server URL — resolve the avatar key via the presign endpoint.
    if (!reduxUser.avatar) return;
    avatarHydratedRef.current = reduxUser.id;
    const capturedAvatar = reduxUser.avatar;
    (async () => {
      try {
        const url = await getAvatarUrl(capturedAvatar);
        // Identity check at commit time: the store's user.avatar may have
        // changed (logout → root-reducer reset cleared user, or new user
        // with a different avatar). Passive useEffect cleanup is too late
        // — a promise can resolve in the window between a synchronous
        // Redux state change and React running cleanup.
        if (store.getState().userStore.user.avatar !== capturedAvatar) {
          return;
        }
        if (url) dispatch(SET_USER_AVATAR_URL(url));
      } catch {
        // avatar failure does not block hydration
      }
    })();
  }, [
    reduxUser.avatar,
    reduxUser.id,
    reduxUser.userPreferences?.avatarUrl,
    dispatch,
    SET_USER_AVATAR_URL,
    store,
  ]);

  const { disconnectWebSocket } = useWebSocketLogout();
  useEffect(() => {
    const handler = (event: StorageEvent): void => {
      if (event.key !== AUTH_CONSTANTS.JWT_TOKEN) return;
      if (event.newValue !== null) return;
      disconnectWebSocket();
      void logout();
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [disconnectWebSocket, logout]);

  return null;
};

export default UserHydrationGate;
